import { ZIndexes } from 'enums/z-indexes';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { LockedLockIcon, MessageIcon } from 'assets/icons';
import { Button, Grid, Typography, styled } from '@mui/material';
import { MessageBadgeLeft } from 'components/styled/message-badges';
import { creatorProfileSelector } from '../store/creators.selectors';
import { newMessagesCountSelector } from 'app/messages/store/messages.selectors';
import { NavigateMessagesHandlerProps } from 'app/messages/types/navigate-messages-handler.props';

const StyledAside = styled('aside')(({ theme }) => ({
  display: 'none',
  flex: '1 1 400px',
  flexDirection: 'column',
  padding: '20px',
  gap: '4px',
  position: 'sticky',
  top: 0,
  zIndex: ZIndexes.External_Top_Second,
  [theme.breakpoints.up('lg')]: {
    display: 'flex',
  },
}));

const ButtonContainer = styled(Grid)({
  display: 'flex',
  flexDirection: 'column',
  padding: '20px',
  background: '#343434',
  borderRadius: '14px',
  zIndex: ZIndexes.External_Top_Second,
});

const StyledButton = styled(Button)({
  marginTop: '15px',
  justifyContent: 'flex-start',
  padding: '6px',
  lineHeight: '22px',
});

interface CreatorProfileAsideProps {
  isConversationEnabled: boolean;
  isToShowUnsubscribedUI: boolean;
  handleOpenSubscribe: () => void;
  navigateMessageCreator: ({
    conversationId,
    creatorId,
  }: NavigateMessagesHandlerProps) => void;
}

const CreatorProfileAside = (props: CreatorProfileAsideProps) => {
  const {
    isConversationEnabled,
    isToShowUnsubscribedUI,
    handleOpenSubscribe,
    navigateMessageCreator,
  } = props;
  const { t } = useTranslation();

  const profile = useSelector(creatorProfileSelector);
  const newMessagesCounter = useSelector(newMessagesCountSelector);

  if (!profile) {
    return <></>;
  }
  return (
    <StyledAside>
      <br />
      <br />

      {isToShowUnsubscribedUI && (
        <ButtonContainer item>
          <Typography
            variant="body2"
            component="p"
            color="dark-white"
            textTransform="uppercase"
            fontFamily="Avenir"
          >
            {t('titles.view-my-camera-roll')}:
          </Typography>
          <StyledButton
            variant="contained"
            color="green"
            size="small"
            startIcon={
              <LockedLockIcon style={{ width: '32px', height: '32px' }} />
            }
            onClick={handleOpenSubscribe}
          >
            {t(
              profile?.subscription?.periodEnd
                ? 'buttons.resubscribe'
                : 'buttons.unlock-roll'
            )}
          </StyledButton>
        </ButtonContainer>
      )}

      {isConversationEnabled && (
        <ButtonContainer item>
          <Typography
            variant="body2"
            component="p"
            color="dark-white"
            textTransform="uppercase"
            sx={{ fontFamily: 'Avenir' }}
          >
            {t('titles.private-dms')}:
          </Typography>
          <StyledButton
            variant="contained"
            color="green"
            size="small"
            startIcon={
              <MessageIcon style={{ width: '32px', height: '32px' }} />
            }
            onClick={() =>
              navigateMessageCreator({
                conversationId: profile.conversation.id,
                creatorId: profile.id,
              })
            }
          >
            {t('buttons.message-me')}
            <MessageBadgeLeft badgeContent={newMessagesCounter} color="error" />
          </StyledButton>
        </ButtonContainer>
      )}
    </StyledAside>
  );
};

export default CreatorProfileAside;
